// DESCRIPTION: Shared helper for the frontend event-list bundle — turns an
// DESCRIPTION: event's per-event `styling` tokens into inline CSS custom properties.

/**
 * Maps a `styling.card_radius` / `styling.button_radius` token to the CSS value it
 * should set. Matches the Tailwind default scale used by `tailwind.config.js`.
 *
 * @type {Object<string, string>}
 */
const RADIUS_TOKEN_VALUES = {
	sharp: '0',
	rounded: '0.5rem',
	pill: '9999px',
};

/**
 * Maps a `styling.shadow` token to a CSS box-shadow value.
 * Matches the elevation scale in `tailwind.config.js`.
 *
 * @type {Object<string, string>}
 */
const SHADOW_TOKEN_VALUES = {
	none: 'none',
	low: '0 1px 2px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.04)',
	medium: '0 4px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.06)',
	high: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)',
};

/**
 * Maps a `styling.spacing` token to a multiplier applied via calc() in the
 * hero CSS (see event-hero.css — every padding uses calc(theme() * var())).
 *
 * @type {Object<string, number>}
 */
const SPACING_TOKEN_VALUES = {
	compact: 0.8,
	normal: 1,
	loose: 1.25,
};

/**
 * Maps a `styling.heading_size` token to a rem font-size.
 *
 * @type {Object<string, string>}
 */
const HEADING_SIZE_TOKEN_VALUES = {
	sm: '1.5rem',
	md: '2rem',
	lg: '2.5rem',
};

/**
 * Maps a `styling.heading_weight` token to a CSS font-weight number.
 *
 * @type {Object<string, string>}
 */
const HEADING_WEIGHT_TOKEN_VALUES = {
	normal: '400',
	medium: '500',
	semibold: '600',
	bold: '700',
};

/**
 * Build inline CSS custom properties from an event's per-event styling
 * tokens (`event.styling`), to be spread onto a card/list-item root.
 *
 * Empty/unset tokens are omitted entirely so the defaults defined in
 * event-hero.css keep applying unchanged.
 *
 * @param {Object} event Event object, possibly with a `styling` sub-object.
 *
 * @return {Object} Inline style object of `--nb-*` custom properties.
 */
export function getEventStyleVars( event ) {
	const styling = event && event.styling ? event.styling : {};
	const vars = {};

	// Color tokens — set directly as CSS custom properties. Each maps to one
	// visible element so admins can style them independently.
	if ( styling.header_background ) {
		vars[ '--nb-accent' ] = styling.header_background;
	}
	if ( styling.header_title_text ) {
		vars[ '--nb-title-text' ] = styling.header_title_text;
	}
	if ( styling.tag_text ) {
		vars[ '--nb-pill-text' ] = styling.tag_text;
	}
	if ( styling.tag_background ) {
		vars[ '--nb-pill-bg' ] = styling.tag_background;
	}
	if ( styling.detail_text ) {
		vars[ '--nb-text-secondary' ] = styling.detail_text;
	}
	if ( styling.price_text ) {
		vars[ '--nb-price-text' ] = styling.price_text;
	}
	if ( styling.button_background ) {
		vars[ '--nb-button-bg' ] = styling.button_background;
	}
	if ( styling.button_text ) {
		vars[ '--nb-button-text' ] = styling.button_text;
	}
	if ( styling.card_background ) {
		vars[ '--nb-surface' ] = styling.card_background;
	}
	if ( styling.border_color ) {
		vars[ '--nb-border-subtle' ] = styling.border_color;
	}

	// Shape tokens.
	if ( styling.card_radius && RADIUS_TOKEN_VALUES[ styling.card_radius ] ) {
		vars[ '--nb-radius' ] = RADIUS_TOKEN_VALUES[ styling.card_radius ];
	}
	if (
		styling.button_radius &&
		RADIUS_TOKEN_VALUES[ styling.button_radius ]
	) {
		vars[ '--nb-button-radius' ] =
			RADIUS_TOKEN_VALUES[ styling.button_radius ];
	}
	if ( styling.shadow && SHADOW_TOKEN_VALUES[ styling.shadow ] ) {
		vars[ '--nb-shadow' ] = SHADOW_TOKEN_VALUES[ styling.shadow ];
	}

	// Spacing density multiplier.
	if ( styling.spacing && SPACING_TOKEN_VALUES[ styling.spacing ] ) {
		vars[ '--nb-spacing-mult' ] = SPACING_TOKEN_VALUES[ styling.spacing ];
	}

	// Typography tokens.
	if (
		styling.heading_size &&
		HEADING_SIZE_TOKEN_VALUES[ styling.heading_size ]
	) {
		vars[ '--nb-heading-size' ] =
			HEADING_SIZE_TOKEN_VALUES[ styling.heading_size ];
	}
	if (
		styling.heading_weight &&
		HEADING_WEIGHT_TOKEN_VALUES[ styling.heading_weight ]
	) {
		vars[ '--nb-heading-weight' ] =
			HEADING_WEIGHT_TOKEN_VALUES[ styling.heading_weight ];
	}
	return vars;
}
